import React, { useState } from "react";
import { Mail, KeyRound, Eye, EyeOff, UserPlus, ArrowLeft } from "lucide-react";
import { fetchJSON } from "../utils/api";

// Staff self-registration (docs/modules/ld-selfservice.md): email in the
// active staff master → emailed code → set password → signed in with the
// 'staff' role (the new-request submit keys + ld:submit). Rendered
// by Login.js as an alternate card.

const inputCls =
  "appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm";

const Register = ({ onBackToLogin }) => {
  const [step, setStep] = useState("email"); // email | code | done
  const [email, setEmail] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const requestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const data = await fetchJSON("/api/auth/register/request", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      setMaskedEmail(data.email);
      setStep("code");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const complete = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await fetchJSON("/api/auth/register/complete", {
        method: "POST",
        body: JSON.stringify({ email, otp, password }),
      });
      // Cookie is set — a full reload runs the auth probe and lands signed in.
      // Reload rather than redirect to "/": someone who scanned a training QR
      // code is sitting on /checkin/<token>, and sending them home would make
      // them scan again.
      window.location.reload();
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <img
            src={`${process.env.PUBLIC_URL}/pkf-logo.svg`}
            alt="PKF Logo"
            className="h-12 w-auto"
            onError={(e) => { e.target.style.display = "none"; }}
          />
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Create Your Account
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          For PKF staff — use your work email from the staff records.
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {step === "email" && (
            <form className="space-y-6" onSubmit={requestCode}>
              <div>
                <label className="block text-sm font-medium text-gray-700">Work Email</label>
                <div className="mt-1 relative">
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`${inputCls} pl-10`}
                    placeholder="your.name@pkf.com"
                    autoComplete="email"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  A verification code will be sent to this address. Registration is only
                  possible for emails listed in the staff records.
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800">{error}</div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center gap-2 py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <UserPlus className="h-4 w-4" />
                {loading ? "Sending Code…" : "Send Verification Code"}
              </button>
            </form>
          )}

          {step === "code" && (
            <form className="space-y-6" onSubmit={complete}>
              <p className="text-sm text-gray-600">
                If <span className="font-medium">{maskedEmail}</span> is a staff email without an account,
                a 6-digit code is on its way — enter it below and choose a password. If you already have an
                account, check your inbox for a reminder and sign in instead.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700">Verification Code</label>
                <div className="mt-1 relative">
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    required
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    className={`${inputCls} pl-10 tracking-widest`}
                    placeholder="123456"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <KeyRound className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Password</label>
                <div className="mt-1 relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`${inputCls} pr-10`}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  At least 12 characters with an uppercase letter, a lowercase letter and a digit.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Confirm Password</label>
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className={`${inputCls} mt-1`}
                  autoComplete="new-password"
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800">{error}</div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Creating Account…" : "Create Account & Sign In"}
              </button>
              <button
                type="button"
                onClick={() => { setStep("email"); setError(""); }}
                className="w-full text-center text-sm text-gray-500 hover:text-gray-700"
              >
                Use a Different Email
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={onBackToLogin}
              className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-500"
            >
              <ArrowLeft className="h-4 w-4" /> Back to Sign In
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
